/**
 * Markup for the shop, pass and challenge side panels, and for the progression
 * block on the full-time screen.
 *
 * Strings, not DOM: the same rule ./net-panels and ./training follow, and for
 * the same reason — a panel that builds elements cannot be asserted on in Node,
 * and every one of these is a pure function of a `ProgressionState` that a test
 * can construct for any instant it likes.
 *
 * The type scale, the tabular figures and the hard right edge are ./style's;
 * nothing here sets a font or a colour that is not a rarity or a team.
 */

import { PASS_TIERS, TIER_POINTS_PER_TIER } from '../game/cosmetics';
import { availableTierPoints } from '../game/challenges';
import { RARITY_HEX } from '../game/items';
import {
  challengeRows,
  passRewardLabel,
  passWindow,
  progressLabel,
  rarityLabel,
  shopRows,
  tierBarFraction,
  tierLabel,
  untilLabel,
  type ProgressionState,
  type ProgressionSummary,
  type ShopRow,
} from './progression-model';

/** Escape the four characters that can break out of a text node or attribute. */
const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const hex = (value: number): string => `#${value.toString(16).padStart(6, '0')}`;

const pct = (t: number): string => `${(Math.min(1, Math.max(0, t)) * 100).toFixed(2)}%`;

const stat = (key: string, value: string): string =>
  `<div class="rw-stat"><div class="rw-stat-key">${esc(key)}</div>` +
  `<div class="rw-stat-val">${esc(value)}</div></div>`;

// ---------------------------------------------------------------------------
// Shop
// ---------------------------------------------------------------------------

/**
 * The shop panel: what you have, when it turns over, and the two rotations as
 * cards.
 *
 * The countdown is computed from the state's own `now`, never from a fresh
 * clock read, so it agrees with every expiry test on the same render.
 */
export const shopPanelMarkup = (state: ProgressionState): string => {
  const rows = shopRows(state);

  const card = (row: ShopRow): string => {
    const owned = row.problem === 'owned';
    const price =
      row.entry.wasPrice === undefined
        ? `<b>${row.entry.price}</b>`
        : `<s>${row.entry.wasPrice}</s> <b>${row.entry.price}</b>`;
    return (
      `<div class="rw-shop-card${owned ? ' owned' : ''}" ` +
      `style="--rw-rarity:${hex(RARITY_HEX[row.rarity])}">` +
      `<div class="rw-shop-rarity">${esc(rarityLabel(row.rarity))}</div>` +
      `<div class="rw-shop-name">${esc(row.name)}</div>` +
      `<div class="rw-shop-price">${owned ? 'Owned' : price}</div>` +
      `</div>`
    );
  };

  const grid = (title: string, which: 'featured' | 'daily'): string => {
    const of = rows.filter(r => r.row === which);
    if (of.length === 0) return '';
    return (
      `<div class="rw-shop-head">${esc(title)}</div>` +
      `<div class="rw-shop-grid">${of.map(card).join('')}</div>`
    );
  };

  return (
    `<div class="rw-stats">` +
    stat('Credits', String(state.profile.wallet.credits)) +
    stat('Refreshes In', untilLabel(state.now, state.shopRefreshAt)) +
    stat('Owned', String(state.profile.inventory.length)) +
    `</div>` +
    grid('Featured', 'featured') +
    grid('Daily', 'daily')
  );
};

// ---------------------------------------------------------------------------
// Rocket Pass
// ---------------------------------------------------------------------------

/**
 * The tier track: a window of tiers around the cursor, with the free row above
 * the premium one and a claim state on each cell.
 *
 * A window rather than all seventy because the pass does not stop at seventy —
 * Pro Tiers continue — so "draw every tier" has no end, and seventy columns
 * would not fit in any case.
 */
export const passPanelMarkup = (state: ProgressionState, cursor: number, count = 9): string => {
  const progress = state.profile.progress;
  const rows = passWindow(state, cursor, count);

  const cell = (track: { reward: unknown; claimed: boolean; claimable: boolean }, reached: boolean): string => {
    const classes = ['rw-tier-cell'];
    if (track.reward === null) classes.push('empty');
    if (track.claimed) classes.push('claimed');
    else if (track.claimable) classes.push('ready');
    else if (!reached) classes.push('locked');
    return `<div class="${classes.join(' ')}"></div>`;
  };

  const columns = rows
    .map(row => {
      const classes = ['rw-tier-col'];
      if (row.tier === cursor) classes.push('on');
      if (row.pro !== null) classes.push('pro');
      return (
        `<div class="${classes.join(' ')}">` +
        cell(row.free, row.reached) +
        cell(row.premium, row.reached) +
        `<div class="rw-tier-num">${row.pro === null ? row.tier : `P${row.pro}`}</div>` +
        `</div>`
      );
    })
    .join('');

  const selected = rows.find(r => r.tier === cursor);
  const detail = selected
    ? `<div class="rw-tier-detail">` +
      `<div class="rw-tier-title">${esc(tierLabel(cursor))}</div>` +
      `<div class="rw-tier-line"><span>Free</span><b>${esc(passRewardLabel(selected.free.reward))}</b></div>` +
      `<div class="rw-tier-line${progress.passPremium ? '' : ' dim'}"><span>Premium</span>` +
      `<b>${esc(passRewardLabel(selected.premium.reward))}</b></div>` +
      `</div>`
    : '';

  return (
    `<div class="rw-stats">` +
    stat('Tier', progress.passTier > PASS_TIERS ? `Pro ${progress.passTier - PASS_TIERS}` : String(progress.passTier)) +
    stat('Tier Points', `${progress.tierPoints} / ${TIER_POINTS_PER_TIER}`) +
    stat('Level', String(progress.level)) +
    stat('Credits', String(state.profile.wallet.credits)) +
    stat('Track', progress.passPremium ? 'Premium' : 'Free') +
    `</div>` +
    `<div class="rw-progress"><i style="width:${pct(tierBarFraction(state))}"></i></div>` +
    `<div class="rw-tier-legend"><span>Free</span><span>Premium</span></div>` +
    `<div class="rw-tier-track">${columns}</div>` +
    detail +
    `<div class="rw-earned">Credits are earned in matches, from challenges and from this pass. ` +
    `There is no way to buy them.</div>`
  );
};

// ---------------------------------------------------------------------------
// Challenges
// ---------------------------------------------------------------------------

export const challengePanelMarkup = (state: ProgressionState): string => {
  const rows = challengeRows(state);
  const ready = rows.filter(r => r.complete && !r.claimed && !r.expired);
  const live = rows.filter(r => !r.expired && !r.claimed);

  const group = (title: string, kind: 'weekly' | 'season' | 'event'): string => {
    const of = rows.filter(r => r.def.kind === kind);
    if (of.length === 0) return '';
    return (
      `<div class="rw-chal-head">${esc(title)}</div>` +
      of
        .map(row => {
          const classes = ['rw-chal'];
          if (row.claimed) classes.push('claimed');
          else if (row.expired) classes.push('expired');
          else if (row.complete) classes.push('ready');
          return (
            `<div class="${classes.join(' ')}">` +
            `<div class="rw-chal-name">${esc(row.def.name)}</div>` +
            `<div class="rw-chal-bar"><i style="width:${pct(row.entry.value / Math.max(1, row.def.target))}"></i></div>` +
            `<div class="rw-chal-count">${esc(progressLabel(row))}</div>` +
            `<div class="rw-chal-tp">${row.tierPoints} TP</div>` +
            `</div>`
          );
        })
        .join('')
    );
  };

  return (
    `<div class="rw-stats">` +
    stat('Ready', String(ready.length)) +
    stat('Live', String(live.length)) +
    stat('Weekly Reset', untilLabel(state.now, state.weekEndsAt)) +
    stat('Season Stage', `${state.seasonStage}`) +
    stat('Tier Points Left', String(availableTierPoints(state.profile, state.now))) +
    `</div>` +
    group('Weekly', 'weekly') +
    group('Season', 'season') +
    group('Event', 'event')
  );
};

// ---------------------------------------------------------------------------
// Full time
// ---------------------------------------------------------------------------

/**
 * What the match just paid, on the scoreboard screen.
 *
 * Every number here is a DIFFERENCE, which is why it comes from the summary the
 * runtime built at the moment of the award rather than from the profile: a
 * profile can say what you have, never what you just gained.
 */
export const summaryMarkup = (summary: ProgressionSummary | null): string => {
  if (summary === null) return '';

  const lines = [
    `<div class="rw-sum-line"><span>XP</span><b>+${summary.xpGained}</b></div>`,
    summary.levelsGained > 0
      ? `<div class="rw-sum-line up"><span>Level Up</span><b>${summary.level}</b></div>`
      : `<div class="rw-sum-line"><span>Level</span><b>${summary.level}</b></div>`,
    `<div class="rw-sum-line"><span>Credits</span><b>+${summary.creditsGained}</b></div>`,
    `<div class="rw-sum-line"><span>Tier Points</span><b>+${summary.tierPointsGained}</b></div>`,
    summary.tiersGained > 0
      ? `<div class="rw-sum-line up"><span>Pass Tier</span><b>${summary.passTier}</b></div>`
      : `<div class="rw-sum-line"><span>Pass Tier</span><b>${summary.passTier}</b></div>`,
  ];

  const challenges = summary.challengesCompleted
    .map(name => `<div class="rw-sum-tag">${esc(name)}</div>`)
    .join('');
  const drops = summary.drops.map(name => `<div class="rw-sum-tag drop">${esc(name)}</div>`).join('');

  return (
    `<div class="rw-summary">` +
    `<div class="rw-sum-head">Progression</div>` +
    lines.join('') +
    (challenges ? `<div class="rw-sum-head">Challenges Completed</div>${challenges}` : '') +
    (drops ? `<div class="rw-sum-head">Dropped</div>${drops}` : '') +
    `</div>`
  );
};
